import { FormEvent, useEffect, useMemo, useState } from "react";
import { Building2, Edit, Plus, RefreshCw, Search, Trash2, UserCog, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminStorePermissionCatalogItem,
  AdminStoreRoleOption,
  AdminStoreUser,
  AdminStoreUserLocation,
  AdminStoreUserPayload,
  AdminStoreUserRole,
  AdminUser,
  AdminApiError,
  PaginationMeta,
  createAdminStoreUser,
  deleteAdminStoreUser,
  getCurrentAdmin,
  listAdminStoreUsers,
  logout,
  updateAdminStoreUser,
} from "./api";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import AdminPasswordInput from "./components/AdminPasswordInput";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";

type StoreUserFormState = {
  Username: string;
  DisplayName: string;
  Email: string;
  Phone: string;
  Role: AdminStoreUserRole;
  IsActive: boolean;
  Password: string;
  LocationIds: string[];
  PrimaryLocationId: string;
  Permissions: string[];
};

const emptyForm: StoreUserFormState = {
  Username: "",
  DisplayName: "",
  Email: "",
  Phone: "",
  Role: "Staff",
  IsActive: true,
  Password: "",
  LocationIds: [],
  PrimaryLocationId: "",
  Permissions: [],
};

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

const fallbackRoleOptions: AdminStoreRoleOption[] = [
  { Value: "StoreManager", Label: "店長 / 門市主管" },
  { Value: "Staff", Label: "門市同仁" },
  { Value: "Audiologist", Label: "聽力師" },
  { Value: "Support", Label: "營運支援" },
];

const permissionDependencies: Record<string, string> = {
  "store-reservations-write": "store-reservations",
  "store-customers-write": "store-customers",
  "store-service-records-write": "store-service-records",
  "store-service-records-void": "store-service-records",
  "store-orders-write": "store-orders",
  "store-orders-void": "store-orders",
  "store-follow-ups-write": "store-follow-ups",
};

const permissionDependents = Object.entries(permissionDependencies).reduce<Record<string, string[]>>((result, [child, parent]) => {
  result[parent] = [...(result[parent] || []), child];
  return result;
}, {});

function normalizePermissionSelection(keys: string[]) {
  const next = new Set(keys);
  Object.entries(permissionDependencies).forEach(([child, parent]) => {
    if (next.has(child)) next.add(parent);
  });
  return Array.from(next);
}

export default function AdminStoreUsers() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [users, setUsers] = useState<AdminStoreUser[]>([]);
  const [counts, setCounts] = useState({ Active: 0, Inactive: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [locations, setLocations] = useState<AdminStoreUserLocation[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<AdminStorePermissionCatalogItem[]>([]);
  const [roleOptions, setRoleOptions] = useState<AdminStoreRoleOption[]>(fallbackRoleOptions);
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [keyword, setKeyword] = useState("");
  const [filterLocationIds, setFilterLocationIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageMessage, setPageMessage] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [editingUser, setEditingUser] = useState<AdminStoreUser | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<StoreUserFormState>(emptyForm);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const selectedPermissionCount = form.Permissions.length;
  const roleLabelMap = useMemo(() => Object.fromEntries(roleOptions.map((item) => [item.Value, item.Label])), [roleOptions]);

  async function load(filters = { Status: status, Role: role, Keyword: keyword, LocationIds: filterLocationIds, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");

    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminStoreUsers(filters);
      setUsers(data.Users);
      setCounts(data.Counts || { Active: 0, Inactive: 0 });
      setPagination(data.Pagination || defaultPagination);
      setLocations(data.Locations || []);
      setPermissionCatalog(data.PermissionCatalog || []);
      setRoleOptions(data.RoleOptions?.length ? data.RoleOptions : fallbackRoleOptions);
    } catch (error) {
      if (error instanceof Error) setPageMessage(error.message);
      if (error instanceof AdminApiError && error.status === 401) {
        navigate("/admin-login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Role: "", Keyword: "", LocationIds: [], Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function defaultPermissions(nextRole: AdminStoreUserRole) {
    if (nextRole === "StoreManager") return permissionCatalog.map((item) => item.Key);
    if (nextRole === "Audiologist") {
      return [
        "store-dashboard",
        "store-reservations",
        "store-reservations-write",
        "store-customers",
        "store-customers-write",
        "store-service-records",
        "store-service-records-write",
        "store-follow-ups",
        "store-follow-ups-write",
        "store-announcements",
      ];
    }
    if (nextRole === "Support") {
      return [
        "store-dashboard",
        "store-reservations",
        "store-reservations-write",
        "store-customers",
        "store-customers-write",
        "store-follow-ups",
        "store-follow-ups-write",
        "store-announcements",
      ];
    }
    return [
      "store-dashboard",
      "store-reservations",
      "store-reservations-write",
      "store-customers",
      "store-customers-write",
      "store-service-records",
      "store-service-records-write",
      "store-orders",
      "store-orders-write",
      "store-follow-ups",
      "store-follow-ups-write",
      "store-announcements",
    ];
  }

  function openCreateForm() {
    const firstLocationId = locations.find((item) => item.IsActive !== false)?.LocationId || locations[0]?.LocationId || "";
    setEditingUser(null);
    setForm({
      ...emptyForm,
      LocationIds: firstLocationId ? [firstLocationId] : [],
      PrimaryLocationId: firstLocationId,
      Permissions: defaultPermissions("Staff"),
    });
    setPageMessage("");
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(user: AdminStoreUser) {
    setEditingUser(user);
    setForm({
      Username: user.Username,
      DisplayName: user.DisplayName,
      Email: user.Email || "",
      Phone: user.Phone || "",
      Role: user.Role,
      IsActive: user.IsActive !== false,
      Password: "",
      LocationIds: user.LocationIds || [],
      PrimaryLocationId: user.PrimaryLocationId || user.LocationIds?.[0] || "",
      Permissions: user.Permissions || [],
    });
    setPageMessage("");
    setFormMessage("");
    setFormOpen(true);
  }

  function toggleLocation(LocationId: string) {
    setForm((current) => {
      const exists = current.LocationIds.includes(LocationId);
      const nextLocationIds = exists ? current.LocationIds.filter((id) => id !== LocationId) : [...current.LocationIds, LocationId];
      const nextPrimary = nextLocationIds.includes(current.PrimaryLocationId) ? current.PrimaryLocationId : nextLocationIds[0] || "";
      return { ...current, LocationIds: nextLocationIds, PrimaryLocationId: nextPrimary };
    });
  }

  function togglePermission(key: string) {
    setForm((current) => {
      const exists = current.Permissions.includes(key);
      const next = new Set(current.Permissions);
      if (exists) {
        next.delete(key);
        (permissionDependents[key] || []).forEach((child) => next.delete(child));
      } else {
        next.add(key);
        const parent = permissionDependencies[key];
        if (parent) next.add(parent);
      }
      return { ...current, Permissions: normalizePermissionSelection(Array.from(next)) };
    });
  }

  function handleRoleChange(nextRole: AdminStoreUserRole) {
    setForm((current) => ({
      ...current,
      Role: nextRole,
      Permissions: editingUser ? current.Permissions : defaultPermissions(nextRole),
    }));
  }

  function validateForm() {
    if (!editingUser && !/^[A-Za-z0-9_.@-]{4,80}$/.test(form.Username.trim())) {
      return "帳號需為 4-80 字元，可使用英文、數字、底線、點、@ 或連字號。";
    }
    if (!form.DisplayName.trim()) return "請輸入門市人員姓名。";
    if (form.Email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Email.trim())) return "Email 格式不正確。";
    if (!editingUser && form.Password.length < 12) return "新增帳號時密碼至少需要 12 個字元。";
    if (editingUser && form.Password && form.Password.length < 12) return "重設密碼至少需要 12 個字元。";
    if (form.LocationIds.length === 0) return "請至少選擇一個所屬門市。";
    if (form.Permissions.length === 0) return "請至少選擇一項門市端權限。";
    return "";
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationMessage = validateForm();
    if (validationMessage) {
      setFormMessage(validationMessage);
      return;
    }

    const payload: AdminStoreUserPayload = {
      Username: form.Username.trim(),
      DisplayName: form.DisplayName.trim(),
      Email: form.Email.trim() || null,
      Phone: form.Phone.trim() || null,
      Role: form.Role,
      IsActive: form.IsActive,
      Password: form.Password || undefined,
      LocationIds: form.LocationIds,
      PrimaryLocationId: form.PrimaryLocationId || form.LocationIds[0] || null,
      Permissions: normalizePermissionSelection(form.Permissions),
    };

    setSaving(true);
    setFormMessage("");
    try {
      if (editingUser) {
        await updateAdminStoreUser(editingUser.StoreUserId, payload);
      } else {
        await createAdminStoreUser(payload);
      }
      setFormOpen(false);
      await load({ Status: status, Role: role, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "儲存失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(user: AdminStoreUser) {
    const confirmed = await confirm({
      title: "刪除門市人員帳號？",
      description: (
        <>
          你即將刪除門市人員帳號 <strong className="font-bold text-[#203d32]">{user.Username}</strong>。
          <br />
          刪除後該帳號將無法登入門市端，既有預約指派紀錄會保留帳號 ID 供追溯。
        </>
      ),
      confirmText: "刪除帳號",
      cancelText: "保留帳號",
      tone: "danger",
    });
    if (!confirmed) return;

    setPageMessage("");
    try {
      await deleteAdminStoreUser(user.StoreUserId);
      await load({ Status: status, Role: role, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "刪除失敗，請稍後再試。");
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Role: role, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: 1, PerPage: pagination.PerPage });
  }

  function toggleFilterLocation(LocationId: string) {
    setFilterLocationIds((current) => (
      current.includes(LocationId) ? current.filter((id) => id !== LocationId) : [...current, LocationId]
    ));
  }

  return (
    <AdminLayout sectionLabel="系統管理" title="門市人員帳號管理" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 sm:grid-cols-2 2xl:flex 2xl:flex-wrap 2xl:items-end 2xl:p-4" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 2xl:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-user-status">狀態</label>
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-user-status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Active">啟用中</option>
            <option value="Inactive">已停用</option>
          </select>
        </div>

        <div className="grid min-w-0 gap-1 2xl:min-w-[170px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-user-role">角色</label>
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-user-role" value={role} onChange={(event) => setRole(event.target.value)}>
            <option value="">全部角色</option>
            {roleOptions.map((item) => <option key={item.Value} value={item.Value}>{item.Label}</option>)}
          </select>
        </div>

        <div className="grid min-w-0 gap-1 sm:col-span-2 2xl:min-w-[300px] 2xl:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="store-user-keyword">關鍵字</label>
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-user-keyword" placeholder="帳號、姓名、Email、電話" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>

        <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] 2xl:w-auto">
          <Search size={18} />
          查詢
        </button>

        <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2] md:px-5" onClick={() => load({ Status: status, Role: role, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
            <RefreshCw size={18} />
            <span className="hidden min-[380px]:inline">重新整理</span>
            <span className="min-[380px]:hidden">整理</span>
          </button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#315746] bg-[#315746] px-3 font-bold text-white transition hover:bg-[#203d32] md:px-5" onClick={openCreateForm} type="button">
            <Plus size={20} />
            新增帳號
          </button>
        </div>

        <div className="min-w-0 border-t border-[#edf0ee] pt-3 sm:col-span-2 2xl:basis-full">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label className="text-sm font-bold text-[#59665f]">所屬門市</label>
            <div className="flex items-center gap-2 text-xs font-bold text-[#315746]">
              <span className="text-[#7b877f]">已選 {filterLocationIds.length} 間</span>
              <button className="hover:underline" onClick={() => setFilterLocationIds(locations.map((item) => item.LocationId))} type="button">全選</button>
              <span className="text-[#c9d2cc]">|</span>
              <button className="hover:underline" onClick={() => setFilterLocationIds([])} type="button">清除</button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {locations.length === 0 ? (
              <span className="rounded-md border border-[#e3e9e5] bg-[#fbfcfb] px-3 py-2 text-sm text-[#7b877f]">尚無門市資料</span>
            ) : locations.map((location) => {
              const checked = filterLocationIds.includes(location.LocationId);
              return (
                <label
                  className={`flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-bold transition ${
                    checked ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#245438]" : "border-[#d8dedb] bg-white text-[#59665f] hover:border-[#b8c8bf] hover:bg-[#fbfcfb]"
                  }`}
                  key={location.LocationId}
                >
                  <input checked={checked} className="h-4 w-4 accent-[#315746]" onChange={() => toggleFilterLocation(location.LocationId)} type="checkbox" />
                  {location.Name}
                  {location.IsActive === false && <span className="text-xs text-[#9a2d2d]">停用</span>}
                </label>
              );
            })}
          </div>
        </div>
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
          <p className="text-xs font-bold text-[#7b877f]">啟用中</p>
          <p className="mt-1 text-2xl font-bold text-[#203d32]">{counts.Active}</p>
        </div>
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
          <p className="text-xs font-bold text-[#7b877f]">已停用</p>
          <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Inactive}</p>
        </div>
      </div>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1120px] border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="px-4 py-3">帳號</th>
                <th className="px-4 py-3">姓名 / 聯絡</th>
                <th className="px-4 py-3">角色</th>
                <th className="px-4 py-3">所屬門市</th>
                <th className="px-4 py-3">權限</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">最後登入</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>載入中...</td></tr>
              ) : users.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={8}>目前沒有符合條件的門市人員帳號。</td></tr>
              ) : (
                users.map((user) => (
                  <tr className="border-t border-[#edf0ee]" key={user.StoreUserId}>
                    <td className="px-4 py-4 align-top font-bold text-[#203d32]">{user.Username}</td>
                    <td className="px-4 py-4 align-top">
                      <div className="font-bold">{user.DisplayName}</div>
                      <div className="mt-1 text-sm leading-5 text-[#59665f]">{user.Email || "-"}<br />{user.Phone || "-"}</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#eef3f8] px-3 py-1 text-xs font-bold text-[#255071]">
                        <UserCog size={13} />
                        {user.RoleLabel || roleLabelMap[user.Role] || user.Role}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="grid gap-1.5">
                        {user.Locations.length === 0 ? <span className="text-sm text-[#9a2d2d]">未設定</span> : user.Locations.map((location) => (
                          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[#f4f6f5] px-2.5 py-1 text-xs font-bold text-[#315746]" key={location.LocationId}>
                            <Building2 size={12} />
                            {location.Name}{location.IsPrimary ? " / 主要" : ""}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top text-sm text-[#59665f]">{user.Permissions.length} 項</td>
                    <td className="px-4 py-4 align-top">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${user.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>
                        {user.IsActive ? "啟用中" : "已停用"}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-top text-sm text-[#59665f]">{user.LastLoginAt || "-"}</td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex flex-wrap gap-2">
                        <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(user)} type="button">
                          <Edit size={16} />編輯
                        </button>
                        <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] transition hover:bg-[#fdebea]" onClick={() => handleDelete(user)} type="button">
                          <Trash2 size={16} />刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Role: role, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: page, PerPage: pagination.PerPage })} />
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#10251c]/35 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <form className="flex max-h-[94vh] w-full max-w-[1440px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.18)] sm:w-[calc(100vw-32px)] sm:rounded-md xl:max-h-[90vh]" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-center justify-between border-b border-[#edf0ee] px-5 py-4">
              <div>
                <p className="text-xs font-bold tracking-[.16em] text-[#7b877f]">STORE USER</p>
                <h2 className="text-xl font-bold text-[#203d32]">{editingUser ? "編輯門市人員" : "新增門市人員"}</h2>
              </div>
              <button aria-label="關閉" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setFormOpen(false)} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium leading-6 text-[#8a1f1f]">{formMessage}</div>}

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-username">帳號</label>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition disabled:bg-[#f4f6f5] focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={Boolean(editingUser)} id="store-username" maxLength={80} value={form.Username} onChange={(event) => setForm((current) => ({ ...current, Username: event.target.value }))} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-display-name">姓名</label>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-display-name" maxLength={80} value={form.DisplayName} onChange={(event) => setForm((current) => ({ ...current, DisplayName: event.target.value }))} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-email">Email</label>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-email" maxLength={190} value={form.Email} onChange={(event) => setForm((current) => ({ ...current, Email: event.target.value }))} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-phone">電話</label>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-phone" maxLength={40} value={form.Phone} onChange={(event) => setForm((current) => ({ ...current, Phone: event.target.value }))} />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(280px,.8fr)_minmax(320px,1fr)]">
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                  <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-role">角色</label>
                  <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-role" value={form.Role} onChange={(event) => handleRoleChange(event.target.value as AdminStoreUserRole)}>
                    {roleOptions.map((item) => <option key={item.Value} value={item.Value}>{item.Label}</option>)}
                  </select>
                </div>
                <AdminSwitchCard checked={form.IsActive} description={form.IsActive ? "啟用後可登入門市端。" : "停用後此帳號將無法登入門市端。"} label="帳號狀態" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="store-password">{editingUser ? "重設密碼（選填）" : "初始密碼"}</label>
                <AdminPasswordInput autoComplete="new-password" className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-password" value={form.Password} onChange={(value) => setForm((current) => ({ ...current, Password: value }))} />
                <p className="mt-2 text-xs text-[#7b877f]">密碼至少 12 個字元。編輯時留空代表不變更密碼。</p>
              </div>

              <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-bold text-[#315746]">所屬門市</p>
                  <span className="text-xs font-bold text-[#7b877f]">{form.LocationIds.length} 間</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {locations.map((location) => {
                    const checked = form.LocationIds.includes(location.LocationId);
                    return (
                      <div className={`rounded-md border px-3 py-2 transition ${checked ? "border-[#9ac8aa] bg-white" : "border-[#e3e9e5] bg-white"}`} key={location.LocationId}>
                        <label className="flex cursor-pointer items-start gap-2 text-sm font-bold text-[#315746]">
                          <input checked={checked} className="mt-1 h-4 w-4 accent-[#315746]" onChange={() => toggleLocation(location.LocationId)} type="checkbox" />
                          <span className="min-w-0">
                            {location.Name}
                            {location.IsActive === false && <span className="ml-1 text-xs text-[#9a2d2d]">停用</span>}
                            <span className="mt-1 block text-xs font-medium text-[#7b877f]">{[location.AddressCity, location.AddressDistrict].filter(Boolean).join(" ") || "-"}</span>
                          </span>
                        </label>
                        {checked && (
                          <label className="mt-2 flex cursor-pointer items-center gap-2 border-t border-[#edf0ee] pt-2 text-xs font-bold text-[#59665f]">
                            <input checked={form.PrimaryLocationId === location.LocationId} className="h-4 w-4 accent-[#315746]" name="primary-location" onChange={() => setForm((current) => ({ ...current, PrimaryLocationId: location.LocationId }))} type="radio" />
                            設為主要門市
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-bold text-[#315746]">門市端權限</p>
                  <div className="flex gap-2 text-xs font-bold text-[#315746]">
                    <button className="hover:underline" onClick={() => setForm((current) => ({ ...current, Permissions: normalizePermissionSelection(permissionCatalog.map((item) => item.Key)) }))} type="button">全選</button>
                    <span className="text-[#c9d2cc]">|</span>
                    <button className="hover:underline" onClick={() => setForm((current) => ({ ...current, Permissions: [] }))} type="button">取消全選</button>
                  </div>
                </div>
                <p className="mb-3 text-xs leading-5 text-[#7b877f]">已選 {selectedPermissionCount} 項；勾選新增、編輯、作廢等操作權限時，系統會自動保留對應查看權限。</p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {permissionCatalog.map((item) => (
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#e3e9e5] bg-white px-3 py-2 text-sm text-[#315746] transition hover:border-[#b8c8bf]" key={item.Key}>
                      <input checked={form.Permissions.includes(item.Key)} className="h-4 w-4 accent-[#315746]" onChange={() => togglePermission(item.Key)} type="checkbox" />
                      {item.Label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setFormOpen(false)} type="button">取消</button>
              <button className="h-10 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">{saving ? "儲存中" : "儲存"}</button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog />
    </AdminLayout>
  );
}
